import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { 
	Box,
	Typography,
	IconButton,
	Button,
	Chip,
	Container,
	Paper,
	Stack,
	CircularProgress,
	Alert,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'
import UserLayout from '../../layouts/UserLayout'

const DetailProduct = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const { id } = useParams()
	const [product, setProduct] = useState(location.state?.product || null)
	const [loading, setLoading] = useState(!location.state?.product)
	const [error, setError] = useState(null)
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

	// Hàm chuyển đổi category_id thành tên danh mục
	const getCategoryName = (categoryId) => {
		const categories = {
			1: 'Nem nướng',
			2: 'Nem sống', 
			3: 'Món ăn kèm',
			4: 'Chả giò'
		}
		return categories[categoryId] || 'Chưa phân loại'
	}

	// Fetch product data if not passed via state
	useEffect(() => {
		const fetchProduct = async () => {
			if (!product && id) {
				try {
					setLoading(true)
					setError(null)
					const response = await apiBase.get(api.ZALO_MINI_APP_PRODUCT_DETAIL(id))
					setProduct(response)
				} catch (error) {
					console.error('Error fetching product:', error)
					setError('Không thể tải thông tin sản phẩm. Vui lòng thử lại.')
				} finally {
					setLoading(false)
				}
			}
		}

		fetchProduct()
	}, [id, product])

	const handleEditClick = () => {
		navigate(`/admin/zalo-mini-app/product/edit/${product.id}`, {
			state: { product },
		})
	}

	const handleDeleteClick = () => {
		setDeleteDialogOpen(true)
	}

	const handleDeleteConfirm = async () => {
		try {
			setLoading(true)
			const response = await apiBase.delete(api.ZALO_MINI_APP_DELETE_PRODUCT(product.id))
			if (response) {
				setDeleteDialogOpen(false)
				navigate('/admin/zalo-mini-app/product')
			} else {
				setError('Có lỗi xảy ra khi xóa sản phẩm')
			}
		} catch (error) {
			console.error('Error deleting product:', error)
			setError('Có lỗi xảy ra khi xóa sản phẩm')
		} finally {
			setLoading(false)
		}
	}

	if (loading) {
		return (
			<UserLayout>
				<Box sx={{ 
					display: 'flex', 
					justifyContent: 'center', 
					alignItems: 'center', 
					minHeight: '50vh',
					flexDirection: 'column',
					gap: 2
				}}>
					<CircularProgress size={50} sx={{ color: 'primary.main' }} />
					<Typography variant="h6" color="text.secondary">
						Đang tải thông tin sản phẩm...
					</Typography>
				</Box>
			</UserLayout>
		)
	}

	if (error || !product) {
		return (
			<UserLayout>
				<Container maxWidth="md" sx={{ py: 3 }}>
					<Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
						{error || 'Không tìm thấy thông tin sản phẩm'}
					</Alert>
					<Button
						variant="outlined"
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/admin/zalo-mini-app/product')}
						sx={{ borderRadius: '12px' }}>
						Quay lại danh sách
					</Button>
				</Container>
			</UserLayout>
		)
	}

	return (
		<UserLayout>
			<Box sx={{ py: 0 }} id = "header-detail-product">
				{/* Header */}
				<Paper
					elevation={0}
					sx={{
						background: 'white',
						color: 'black',
						mb: 2,
						position: 'relative',
						overflow: 'hidden',
					}}>
					<Box sx={{ p: 1.5, position: 'relative', zIndex: 1 }}>
						{/* Top Bar */}
						<Box sx={{ 
							display: 'flex', 
							alignItems: 'center', 
							mb: 1.5,
							gap: 1.5
						}}>
							<IconButton 
								edge='start' 
								color='inherit' 
								onClick={() => navigate(-1)}
								size="small"
								sx={{
									background: 'rgba(255, 255, 255, 0.2)',
									backdropFilter: 'blur(10px)',
									'&:hover': {
										background: 'rgba(255, 255, 255, 0.3)',
									}
								}}>
								<ArrowBackIcon />
							</IconButton>
							<Typography variant='h6' sx={{ flex: 1, fontWeight: 600 }}>
								Chi tiết sản phẩm
							</Typography>
						</Box>

						{/* Product Basic Info */}
						<Box sx={{ 
							display: 'flex', 
							alignItems: 'center',
							gap: 1.5,
							flexWrap: 'wrap'
						}}>
							<Box sx={{ 
								width: 50, 
								height: 50, 
								overflow: 'hidden',
							}}>
								<img
									src={product.image}
									alt={product.name}
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'cover',
									}}
								/>
							</Box>
							<Box>
								<Typography variant='body1' sx={{ fontWeight: 600, mb: 0.5 }}>
									{product.name}
								</Typography>
								<Stack direction='row' spacing={1} alignItems='center'>
									{product.on_sale === 1 && (
										<Chip
											label="GIẢM GIÁ"
											size="small"
											sx={{
												background: 'rgba(255, 255, 255, 0.2)',
												color: 'white',
												fontWeight: 600,
												fontSize: '0.65rem',
												height: 18,
											}}
										/>
									)}
									<Typography variant='caption' sx={{ opacity: 0.9 }}>
										ID: {product.id || product._id}
									</Typography>
								</Stack>
							</Box>
						</Box>
					</Box>
				</Paper>

				{/* Main Content */}
				<Container maxWidth="sm" sx={{ px: 1, pb: 3 }} >
					<Box sx={{ position: 'relative' }}>
						{/* Action Buttons - Top Right Outside */}
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								right: 0,
								display: 'flex',
								flexDirection: 'column',
								gap: 0.5,
								zIndex: 2,
								transform: 'translateY(-8px)',
							}}>
							<IconButton
								onClick={handleEditClick}
								size="medium"
								sx={{
									background: 'rgba(255, 255, 255, 0.95)',
									color: '#1976d2',
									width: 40,
									height: 40,
									boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
									'&:hover': {
										background: 'rgba(255, 255, 255, 1)',
										transform: 'scale(1.1)',
										boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
										color: '#1565c0',
									},
									transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
								}}>
								<EditIcon sx={{ fontSize: 20 }} />
							</IconButton>
							<IconButton
								onClick={handleDeleteClick}
								size="medium"
								sx={{
									background: 'rgba(255, 255, 255, 0.95)',
									color: '#d32f2f',
									width: 40,
									height: 40,
									boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
									'&:hover': {
										background: 'rgba(255, 255, 255, 1)',
										transform: 'scale(1.1)',
										boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
										color: '#c62828',
									},
									transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
								}}>
								<DeleteIcon sx={{ fontSize: 20 }} />
							</IconButton>
						</Box>
						
						<Grid container spacing={1.5} id="detail-product">
							{/* Left Column - Image */}
							<Grid item xs={12} md={5}>
								<Paper
									elevation={0}
									sx={{
										borderRadius: '12px',
										overflow: 'hidden',
										border: '1px solid rgba(0, 0, 0, 0.08)',
									}}>
									<Box
										sx={{
											position: 'relative',
											width: '100%',
											height: { xs: 200, md: 250 },
										}}>
										<img
											src={product.image}
											alt={product.name}
											style={{
												width: '100%',
												height: '100%',
												objectFit: 'cover',
											}}
										/>
										
										{/* Sale Badge */}
										{product.on_sale === 1 && (
											<Chip
												label="GIẢM GIÁ"
												size="small"
												sx={{
													position: 'absolute',
													top: 8,
													left: 8,
													background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
													color: 'white',
													fontWeight: 600,
													fontSize: '0.7rem',
													height: 20,
												}}
											/>
										)}
									</Box>
								</Paper>
							</Grid>

						{/* Right Column - Details */}
						<Grid item xs={12} md={7}>
							<Stack spacing={1.5}>
								{/* Product Name */}
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										borderRadius: '10px',
										border: '1px solid rgba(0, 0, 0, 0.08)',
									}}>
									<Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
										{product.name}
									</Typography>
									
									{/* Price Section */}
									<Box sx={{ mb: 1 }}>
										{product.on_sale === 1 ? (
											<Stack direction='row' spacing={1} alignItems='center' flexWrap="wrap">
												<Typography
													variant='h6'
													sx={{
														textDecoration: 'line-through',
														color: 'text.secondary',
														fontWeight: 400,
													}}>
													{product.price?.toLocaleString()}đ
												</Typography>
												<Typography 
													variant='h5' 
													sx={{ 
														color: '#ff5722',
														fontWeight: 700,
													}}>
													{product.sale_price?.toLocaleString()}đ
												</Typography>
											</Stack>
										) : (
											<Typography 
												variant='h5' 
												sx={{ 
													color: 'primary.main',
													fontWeight: 700,
												}}>
												{product.price?.toLocaleString()}đ
											</Typography>
										)}
									</Box>

									{/* Status */}
									<Stack direction='row' spacing={1} alignItems='center'>
										<Chip
											label={product.status === true ? 'Đang bán' : 'Ngừng bán'}
											color={product.status === true ? 'success' : 'default'}
											variant="outlined"
											size="small"
										/>
									</Stack>
								</Paper>

								{/* Product Details */}
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
									}}>
									<Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
										Thông tin chi tiết
									</Typography>
									
									<Grid container spacing={1}>
										<Grid item xs={6}>
											<Typography variant='caption' color="text.secondary">
												Mã sản phẩm
											</Typography>
											<Typography variant='body2' sx={{ fontWeight: 500 }}>
												{product.id || product._id || 'N/A'}
											</Typography>
										</Grid>
										<Grid item xs={6}>
											<Typography variant='caption' color="text.secondary">
												Danh mục
											</Typography>
											<Typography variant='body2' sx={{ fontWeight: 500 }}>
												{getCategoryName(product.category_id)}
											</Typography>
										</Grid>
										<Grid item xs={6}>
											<Typography variant='caption' color="text.secondary">
												Trạng thái
											</Typography>
											<Typography variant='body2' sx={{ fontWeight: 500 }}>
												{product.status === true ? 'Đang bán' : 'Ngừng bán'}
											</Typography>
										</Grid>
										<Grid item xs={6}>
											<Typography variant='caption' color="text.secondary">
												Khuyến mãi
											</Typography>
											<Typography variant='body2' sx={{ fontWeight: 500 }}>
												{product.on_sale === 1 ? 'Có' : 'Không'}
											</Typography>
										</Grid>
									</Grid>
								</Paper>

								{/* Description */}
								{product.description && (
									<Paper
										elevation={0}
										sx={{
											p: 1.5,
											borderRadius: '10px',
											border: '1px solid rgba(0, 0, 0, 0.08)',
										}}>
										<Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
											Mô tả sản phẩm
										</Typography>
										<Typography variant='body2' color="text.secondary" sx={{ lineHeight: 1.4 }}>
											{product.description}
										</Typography>
									</Paper>
								)}
							</Stack>
						</Grid>
					</Grid>
					</Box>
				</Container>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={deleteDialogOpen}
					onClose={() => setDeleteDialogOpen(false)}
					aria-labelledby="delete-dialog-title"
					aria-describedby="delete-dialog-description">
					<DialogTitle id="delete-dialog-title">
						Xác nhận xóa sản phẩm
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="delete-dialog-description">
							Bạn có chắc chắn muốn xóa sản phẩm "{product.name}"? Hành động này không thể hoàn tác.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)} color="primary">
							Hủy
						</Button>
						<Button 
							onClick={handleDeleteConfirm} 
							color="error" 
							variant="contained"
							disabled={loading}>
							{loading ? <CircularProgress size={20} /> : 'Xóa'}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</UserLayout>
	)
}

export default DetailProduct
